import { useGlobal } from "@/app/providers/use-global";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useScreen } from "@/hooks/use-screen";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/shared/components/user-avatar";
import type { ProfileOrg } from "@/shared/models";
import { useUser } from "@/shared/providers/user-provider";
import { Check } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { dropdownContentStyles } from "../../shared";
import { AutoScrollBack } from "./auto-scroll-back";
import { SystemPromptDialog } from "./system-prompt-dialog";
import { VoiceSettings } from "./voice-settings";

type UserMenuProps = {
  triggerVariant?: "outline" | "ghost";
  triggerClassName?: string;
};

export const UserMenu = ({
  triggerVariant = "ghost",
  triggerClassName,
}: UserMenuProps) => {
  const [isSystemPromptDialogOpen, setIsSystemPromptDialogOpen] =
    useState(false);
  const { stytchUser, logout, profile, changeOrg, currentOrg } = useUser();
  const { isMobile } = useScreen();
  const { isFullScreenIframe } = useGlobal();

  const showcaseName = useMemo(() => {
    if (stytchUser?.name && typeof stytchUser.name === "object") {
      const showcaseName = `${stytchUser.name}`;
      if (showcaseName.trim()) {
        return showcaseName.trim();
      }
    }

    return stytchUser?.email_address || "";
  }, [stytchUser]);

  const userOrgs = useMemo(() => {
    return profile?.user.orgs?.data ?? [];
  }, [profile]);

  const handleChangeOrg = async (org: ProfileOrg) => {
    if (!changeOrg) {
      throw new Error("changeOrg is not defined");
    }
    return await changeOrg(org.id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={triggerVariant}
          className={cn(
            "justify-start gap-[6px] overflow-hidden text-ellipsis hover:bg-inherit hover:text-primaryaccent text-xs p-2",
            triggerVariant === "ghost" && "px-0",
            triggerClassName,
          )}
        >
          <UserAvatar user={profile?.user} />
          <span>{showcaseName}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={cn(
          ` min-w-[200px] max-w-[350px] ${dropdownContentStyles} font-inter`,
          !isFullScreenIframe && "group-[.iframe]:hidden",
        )}
        align={isMobile ? "end" : "start"}
      >
        <DropdownMenuLabel className="font-normal px-6">
          <div className="font-bold font-montserrat">{profile?.user.name}</div>
          <div className="opacity-50">{profile?.user.email}</div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {userOrgs.length >= 2 &&
          userOrgs.map((org) => (
            <Fragment key={org.id}>
              <DropdownMenuItem
                className="gap-2"
                onClick={() => handleChangeOrg(org)}
              >
                {org.id === currentOrg ? (
                  <Check size={16} />
                ) : (
                  <div className="size-[16px]" />
                )}
                {org.name}
              </DropdownMenuItem>
            </Fragment>
          ))}

        <DropdownMenuSeparator />

        <div className="group-[.iframe]:hidden w-full flex justify-between items-center px-6 py-1">
          <span className="text-sm">Theme</span>
          <ThemeToggle />
        </div>

        {isMobile ? (
          <Accordion type="single" collapsible>
            <AccordionItem
              className="border-none data-[state=open]:bg-[#F8F8F7] dark:data-[state=open]:bg-[#2B2D32]"
              value="item-1"
            >
              <AccordionTrigger className="w-full px-6 py-1.5 hover:no-underline text-sm font-normal hover:bg-accent dark:hover:bg-neutral-700 rounded-sm">
                Advanced Settings
              </AccordionTrigger>
              <AccordionContent className="p-0 flex flex-col">
                <div className="my-1" />

                <SystemPromptDialog
                  open={isSystemPromptDialogOpen}
                  onOpenChange={setIsSystemPromptDialogOpen}
                />

                <div className="my-1" />

                <AutoScrollBack />

                <div className="my-1" />

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="font-normal">
                  <div className="font-bold">Voice Settings</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <VoiceSettings />

                <div className="my-1" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="w-full hover:cursor-pointer dark:hover:bg-neutral-700 rounded-sm px-6">
              Advanced Settings
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="min-w-[200px] px-0 dark:text-white dark:bg-shallow dark:border-border font-inter rounded-md">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-bold">Voice Settings</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <VoiceSettings />

                <DropdownMenuSeparator />
                <SystemPromptDialog
                  open={isSystemPromptDialogOpen}
                  onOpenChange={setIsSystemPromptDialogOpen}
                />

                <AutoScrollBack />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
        <DropdownMenuItem
          className="w-full hover:cursor-pointer dark:hover:bg-neutral-700 rounded-sm px-6"
          onClick={() => logout()}
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
